import React, { useEffect, useMemo, useState } from 'react';
import { FiEdit, FiPlus, FiTrash2, FiUser, FiX } from 'react-icons/fi';
import axios from 'axios';
import Swal from 'sweetalert2';
import { API_ENDPOINTS } from '../../utils/api';

const emptyForm = () => ({
  empNo: '',
  name: '',
  role: 'Salesman',
  phone: '',
  email: ''
});

const formatSno = (index) => String(index + 1).padStart(2, '0');

const Salesman = () => {
  const [salesmen, setSalesmen] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selected, setSelected] = useState(null);
  const [formData, setFormData] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  const authHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('token')}`
  });

  const fetchSalesmen = async () => {
    try {
      setLoading(true);
      const response = await axios.get(API_ENDPOINTS.getSalesmen, {
        headers: authHeaders()
      });
      setSalesmen(response.data || []);
    } catch (error) {
      console.error('Error fetching salesmen:', error);
      Swal.fire('Error', 'Failed to fetch salesmen', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSalesmen();
  }, []);

  const sortedSalesmen = useMemo(() => {
    return [...salesmen].sort((a, b) => {
      const aNum = Number.parseInt(String(a.sno || '').replace(/\D/g, ''), 10) || 0;
      const bNum = Number.parseInt(String(b.sno || '').replace(/\D/g, ''), 10) || 0;
      return aNum - bNum;
    });
  }, [salesmen]);

  const openCreate = () => {
    setSelected(null);
    setFormData(emptyForm());
    setShowModal(true);
  };

  const openEdit = (row) => {
    setSelected(row);
    setFormData({
      empNo: row.empNo || '',
      name: row.name || '',
      role: row.role || 'Salesman',
      phone: row.phone || '',
      email: row.email || ''
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelected(null);
    setFormData(emptyForm());
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.empNo.trim() || !formData.name.trim()) {
      Swal.fire('Error', 'Emp no and Salesman name are required', 'error');
      return;
    }

    setSaving(true);
    try {
      if (selected?._id) {
        await axios.put(API_ENDPOINTS.updateSalesman(selected._id), formData, {
          headers: authHeaders()
        });
        Swal.fire('Success', 'Salesman updated successfully', 'success');
      } else {
        await axios.post(API_ENDPOINTS.createSalesman, formData, {
          headers: authHeaders()
        });
        Swal.fire('Success', 'Salesman created successfully', 'success');
      }
      closeModal();
      fetchSalesmen();
    } catch (error) {
      console.error('Error saving salesman:', error);
      Swal.fire('Error', error.response?.data?.error || 'Failed to save salesman', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: 'This salesman will be removed',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete'
    });

    if (!result.isConfirmed) return;

    try {
      await axios.delete(API_ENDPOINTS.deleteSalesman(id), {
        headers: authHeaders()
      });
      setSalesmen((prev) =>
        prev
          .filter((row) => row._id !== id)
          .map((row, index) => ({ ...row, sno: formatSno(index) }))
      );
      Swal.fire('Deleted', 'Salesman deleted successfully', 'success');
      fetchSalesmen();
    } catch (error) {
      console.error('Error deleting salesman:', error);
      Swal.fire('Error', 'Failed to delete salesman', 'error');
    }
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-3">
            <FiUser className="text-blue-600" />
            Salesman
          </h1>
          <p className="text-gray-600 mt-1">Manage salesman details for invoices and field sales</p>
        </div>
        <button
          onClick={openCreate}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2"
        >
          <FiPlus /> Add Salesman
        </button>
      </div>

      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 border-b">
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">SNO</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Emp no</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Salesman</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Role</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Phone no</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Email id</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedSalesmen.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="text-center py-12 text-gray-500">
                      No salesmen found. Click Add Salesman to create one.
                    </td>
                  </tr>
                ) : (
                  sortedSalesmen.map((row, index) => (
                    <tr key={row._id} className="border-b hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-700">{formatSno(index)}</td>
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">{row.empNo}</td>
                      <td className="px-4 py-3 text-sm text-gray-900">{row.name}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">{row.role || '—'}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">{row.phone || '—'}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">{row.email || '—'}</td>
                      <td className="px-4 py-3">
                        <div className="flex gap-2">
                          <button
                            onClick={() => openEdit(row)}
                            className="text-green-600 hover:text-green-800"
                            title="Edit"
                          >
                            <FiEdit />
                          </button>
                          <button
                            onClick={() => handleDelete(row._id)}
                            className="text-red-600 hover:text-red-800"
                            title="Delete"
                          >
                            <FiTrash2 />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-800">
                {selected ? 'Edit Salesman' : 'Add Salesman'}
              </h2>
              <button type="button" onClick={closeModal} className="text-gray-400 hover:text-gray-600">
                <FiX className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Emp no <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.empNo}
                  onChange={(e) => setFormData({ ...formData, empNo: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g. EMP001"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Salesman <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Salesman name"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                <input
                  type="text"
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Salesman / Manager / ..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone no</label>
                <input
                  type="text"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Phone number"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email id</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="email@example.com"
                />
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-60"
                >
                  {saving ? 'Saving...' : selected ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Salesman;
